import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader, KpiShell, KPI_ROW, PanelLoading } from '../components/ui.jsx'
import { BarChart } from '../components/charts.jsx'
import DataTable, { StatusPill } from '../components/DataTable.jsx'
import Select from '../components/Select.jsx'
import {
  RevenueSignalsIcon,
  ForecastIcon,
  UserIcon,
  MailIcon,
  CheckSquareIcon,
  CalendarIcon,
  TagIcon,
  ClockIcon,
  HashIcon,
  TextIcon,
  BuildingIcon,
} from '../components/icons.jsx'
import { formatMoney, deltaOf } from '../lib/sampleData.js'
import {
  salesSnapshot,
  SALES_RANGE_KEYS,
  SALES_RANGE_LABELS,
  DEFAULT_SALES_RANGE,
  SUBSCRIPTION_STATUSES,
  PAYMENT_STATUSES,
  isSalesRange,
} from '../lib/salesMetrics.js'

// SALES — money in, by what sold it; money out to the bank; and the
// subscriptions behind the recurring part. Read live from Stripe on every
// visit (lib/salesStripe.js) and computed by lib/salesMetrics.js, whose header
// sets out each definition. Every tile says on hover exactly what it counts.
//
// MRR, past-due invoices and what Stripe still holds are AS OF NOW and ignore
// the range; everything else counts the selected range against the
// equal-length window before it, which is where the deltas come from.

const CARD = 'mb-5 max-w-[900px] rounded-[12px] border border-line bg-surface p-5 shadow-card'
const SECTION_H = 'm-0 mb-2 text-[16px] font-semibold text-ink'
const BOX = 'p-10 text-center text-dim'
const LINK = 'text-primary-dark no-underline hover:underline'

const RANGE_OPTIONS = SALES_RANGE_KEYS.map((key) => ({ value: key, label: SALES_RANGE_LABELS[key] }))

const PAYMENT_TONE = { paid: 'success', failed: 'danger', refunded: 'warning', pending: 'neutral' }
const PAYMENT_LABEL = { paid: 'Paid', failed: 'Failed', refunded: 'Refunded', pending: 'Pending' }
const SOURCE_LABEL = {
  subscription: 'Subscription',
  payment_link: 'Payment link',
  checkout: 'Checkout',
  invoice: 'Invoice',
  direct: 'Direct charge',
}
const PAYOUT_TONE = { paid: 'success', in_transit: 'info', pending: 'neutral', failed: 'danger', canceled: 'neutral' }
const PAYOUT_LABEL = { paid: 'Paid', in_transit: 'In transit', pending: 'Pending', failed: 'Failed', canceled: 'Canceled' }
const SUB_TONE = {
  active: 'success',
  past_due: 'warning',
  trialing: 'info',
  paused: 'neutral',
  unpaid: 'danger',
  incomplete: 'neutral',
  incomplete_expired: 'neutral',
  canceled: 'neutral',
}
const SUB_LABEL = {
  active: 'Active',
  past_due: 'Past due',
  trialing: 'Trialing',
  paused: 'Paused',
  unpaid: 'Unpaid',
  incomplete: 'Incomplete',
  incomplete_expired: 'Expired',
  canceled: 'Canceled',
}
// Tabs for the statuses every account has; the rarer ones get a tab only when
// a subscription is in them.
const CORE_SUB_STATUSES = new Set(['active', 'past_due', 'trialing', 'canceled'])

// Money is shown to the cent: on an account this size, rounding $299.40 of MRR
// to "$299" is the kind of small lie this page exists not to tell.
const money = (cents) => formatMoney(cents, { cents: true })
const plural = (n, one, many = `${one}s`) => `${Number(n || 0).toLocaleString()} ${n === 1 ? one : many}`
const INTERVAL = { day: 'day', week: 'week', month: 'mo', year: 'yr' }
const perInterval = (s) => `${money(s.amountCents)}/${s.intervalCount > 1 ? `${s.intervalCount} ` : ''}${INTERVAL[s.interval] || s.interval}`

function When({ iso, dateOnly = false }) {
  if (!iso) return <span className="text-faint">—</span>
  const d = new Date(iso)
  const text = dateOnly
    ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  return (
    <span title={iso} className="whitespace-nowrap">
      {text}
    </span>
  )
}

function StripeLink({ url, children, className = '' }) {
  if (!url) return <span className={className}>{children}</span>
  return (
    <a href={url} target="_blank" rel="noreferrer" className={`${LINK} ${className}`} title="Open in Stripe">
      {children}
    </a>
  )
}

function CustomerCell({ customer }) {
  return (
    <div className="min-w-0">
      <StripeLink url={customer.dashboardUrl} className="font-semibold">
        <span className={customer.dashboardUrl ? '' : 'text-ink'}>{customer.name}</span>
      </StripeLink>
      {customer.email && <div className="mt-[2px] text-[12px] text-faint">{customer.email}</div>}
    </div>
  )
}

const Sub = ({ children }) => <div className="mt-[2px] text-[12px] text-faint">{children}</div>

const PAYMENT_COLUMNS = [
  {
    key: 'customer',
    header: 'Customer',
    icon: BuildingIcon,
    render: (p) => <CustomerCell customer={p.customer} />,
    sortValue: (p) => p.customer.name,
    sortable: true,
  },
  {
    key: 'description',
    header: 'Description',
    icon: TextIcon,
    maxWidth: 340,
    sortable: true,
    sortValue: (p) => `${p.special ? 0 : 1} ${p.description}`,
    render: (p) => (
      <div className="min-w-0">
        <div>{p.description}</div>
        <Sub>
          {SOURCE_LABEL[p.source] || p.source}
          {p.failureMessage ? ` · ${p.failureMessage}` : ''}
        </Sub>
      </div>
    ),
  },
  {
    key: 'amountCents',
    header: 'Amount',
    icon: RevenueSignalsIcon,
    numeric: true,
    sortable: true,
    render: (p) => (
      <div className="tabular-nums">
        <div>{money(p.amountCents)}</div>
        {p.refundedCents > 0 ? (
          <Sub>{p.refundedCents >= p.amountCents ? 'refunded in full' : `${money(p.refundedCents)} refunded`}</Sub>
        ) : p.feeCents > 0 ? (
          <Sub>{money(p.feeCents)} fee</Sub>
        ) : null}
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (p) => <StatusPill tone={PAYMENT_TONE[p.status] || 'neutral'}>{PAYMENT_LABEL[p.status] || p.status}</StatusPill>,
  },
  { key: 'method', header: 'Method', icon: CheckSquareIcon, render: (p) => p.method || <span className="text-faint">—</span> },
  { key: 'createdAt', header: 'When', icon: ClockIcon, sortable: true, render: (p) => <When iso={p.createdAt} /> },
  {
    key: 'id',
    header: 'Payment',
    icon: HashIcon,
    defaultHidden: true,
    render: (p) => (
      <StripeLink url={p.dashboardUrl} className="text-[12.5px]">
        {p.id}
      </StripeLink>
    ),
  },
]

const PAYOUT_COLUMNS = [
  {
    key: 'amountCents',
    header: 'Amount',
    icon: RevenueSignalsIcon,
    numeric: true,
    sortable: true,
    render: (p) => <span className="tabular-nums">{money(p.amountCents)}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (p) => (
      <div>
        <StatusPill tone={PAYOUT_TONE[p.status] || 'neutral'}>{PAYOUT_LABEL[p.status] || p.status}</StatusPill>
        {p.failureMessage && <Sub>{p.failureMessage}</Sub>}
      </div>
    ),
  },
  { key: 'arrivesAt', header: 'Lands', icon: CalendarIcon, sortable: true, render: (p) => <When iso={p.arrivesAt} dateOnly /> },
  { key: 'createdAt', header: 'Sent', icon: ClockIcon, sortable: true, render: (p) => <When iso={p.createdAt} dateOnly /> },
  { key: 'destination', header: 'Bank', icon: BuildingIcon, render: (p) => p.destination || <span className="text-faint">—</span> },
  {
    key: 'id',
    header: 'Payout',
    icon: HashIcon,
    defaultHidden: true,
    render: (p) => (
      <StripeLink url={p.dashboardUrl} className="text-[12.5px]">
        {p.id}
      </StripeLink>
    ),
  },
]

const SUBSCRIPTION_COLUMNS = [
  {
    key: 'customer',
    header: 'Customer',
    icon: BuildingIcon,
    render: (s) => <CustomerCell customer={s.customer} />,
    sortValue: (s) => s.customer.name,
    sortable: true,
  },
  {
    key: 'planName',
    header: 'Plan',
    icon: TagIcon,
    sortable: true,
    render: (s) => (
      <div className="min-w-0">
        <div>{s.deal || s.planName}</div>
        <Sub>
          {perInterval(s)}
          {s.discount ? ` · ${s.discount}` : ''}
          {s.deal ? ' · payment link' : ''}
        </Sub>
      </div>
    ),
  },
  { key: 'seats', header: 'Seats', icon: UserIcon, numeric: true, sortable: true, render: (s) => Number(s.seats || 0).toLocaleString() },
  {
    // What the subscription carries a month. Greyed when it is not counting
    // in MRR right now — a trial, a paused or canceled one, or one whose first
    // invoice has never been paid.
    key: 'mrrCents',
    header: 'MRR',
    icon: RevenueSignalsIcon,
    numeric: true,
    sortable: true,
    render: (s) => (
      <span
        className={`tabular-nums ${s.countsNow ? '' : 'text-faint'}`}
        title={s.countsNow ? 'Counting in MRR now.' : 'Not counting in MRR now — the monthly value it carries while it pays.'}
      >
        {money(s.mrrCents)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    sortValue: (s) => SUBSCRIPTION_STATUSES.indexOf(s.status),
    render: (s) => (
      <div>
        <StatusPill tone={SUB_TONE[s.status] || 'neutral'}>{SUB_LABEL[s.status] || s.status}</StatusPill>
        {s.awaitingFirstPayment && <div className="mt-[3px] text-[12px] text-faint">never paid</div>}
      </div>
    ),
  },
  { key: 'startedAt', header: 'Started', icon: CalendarIcon, sortable: true, render: (s) => <When iso={s.startedAt} dateOnly /> },
  {
    // One column for the date that matters next, whichever it is: a canceled
    // row's end, a trial's end, or the next renewal. Three sparse columns
    // would each be mostly dashes.
    key: 'next',
    header: 'Renews / ends',
    icon: ClockIcon,
    sortable: true,
    sortValue: (s) => s.canceledAt || s.renewsAt || '',
    render: (s) => {
      if (s.status === 'canceled')
        return (
          <span className="text-dim">
            Canceled <When iso={s.canceledAt} dateOnly />
          </span>
        )
      if (s.status === 'trialing')
        return (
          <span>
            Trial ends <When iso={s.renewsAt} dateOnly />
          </span>
        )
      if (s.status === 'paused') return <span className="text-dim">Collection paused</span>
      if (s.status === 'incomplete_expired') return <span className="text-dim">Never started</span>
      return <When iso={s.renewsAt} dateOnly />
    },
  },
  { key: 'method', header: 'Method', icon: CheckSquareIcon, defaultHidden: true, render: (s) => s.method || <span className="text-faint">—</span> },
  {
    key: 'id',
    header: 'Subscription',
    icon: HashIcon,
    defaultHidden: true,
    render: (s) => (
      <StripeLink url={s.dashboardUrl} className="text-[12.5px]">
        {s.id}
      </StripeLink>
    ),
  },
]

const countBy = (rows, key) => {
  const out = {}
  for (const r of rows) out[r[key]] = (out[r[key]] || 0) + 1
  return out
}

const timeOf = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

export default function Sales() {
  const [searchParams, setSearchParams] = useSearchParams()
  const rangeParam = searchParams.get('range')
  // An unknown ?range= is the default, never a guess — and never an error
  // page for a mistyped link.
  const range = isSalesRange(rangeParam) ? rangeParam : DEFAULT_SALES_RANGE
  const [snap, setSnap] = useState(null)
  const [paymentTab, setPaymentTab] = useState('all')
  const [subscriptionTab, setSubscriptionTab] = useState('all')

  useEffect(() => {
    let cancelled = false
    salesSnapshot({ range }).then((s) => {
      if (!cancelled) setSnap(s)
    })
    return () => {
      cancelled = true
    }
  }, [range])

  // Changing the range adjusts this page in place; it is not a new page, so
  // it REPLACES the history entry — Back should leave Sales, not step through
  // every range the reader tried. The default is written as no param at all,
  // so the bare URL and the default range are the same link.
  const setRange = (next) => {
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev)
        if (next === DEFAULT_SALES_RANGE) out.delete('range')
        else out.set('range', next)
        return out
      },
      { replace: true },
    )
  }

  const payments = snap?.available ? snap.payments : []
  const payouts = snap?.available ? snap.payouts : []
  const subscriptions = snap?.available ? snap.subscriptions : []

  const paymentTabs = useMemo(() => {
    const counts = countBy(payments, 'status')
    const special = payments.filter((p) => p.special).length
    return [
      { key: 'all', label: 'All', count: payments.length },
      { key: 'special', label: 'Special deals', count: special, predicate: (p) => p.special },
      { key: 'subscription', label: 'Subscriptions', count: payments.length - special, predicate: (p) => !p.special },
      ...PAYMENT_STATUSES.filter((s) => s === 'refunded' || s === 'failed').map((s) => ({
        key: s,
        label: PAYMENT_LABEL[s],
        count: counts[s] || 0,
        predicate: (p) => p.status === s,
      })),
    ]
  }, [payments])
  const subscriptionTabs = useMemo(() => {
    const counts = countBy(subscriptions, 'status')
    return [
      { key: 'all', label: 'All', count: subscriptions.length },
      ...SUBSCRIPTION_STATUSES.filter((s) => CORE_SUB_STATUSES.has(s) || counts[s]).map((s) => ({
        key: s,
        label: SUB_LABEL[s],
        count: counts[s] || 0,
        predicate: (row) => row.status === s,
      })),
    ]
  }, [subscriptions])
  // The chart gets DOLLARS, not cents. BarChart prints its gridline labels
  // from the raw values, and only the tooltip goes through formatValue — so
  // the series is converted here and the tooltip converts back.
  const revenueBars = useMemo(() => (snap?.available ? snap.series.revenueByDay.map((d) => ({ ...d, value: d.value / 100 })) : []), [snap])
  const dollars = (v) => money(Math.round(v * 100))

  const header = (
    <PageHeader
      title="Sales"
      actions={
        <>
          {snap?.available && (
            <span
              title={[
                `Read from Stripe at ${new Date(snap.asOf).toLocaleString()}. Every figure is computed from the account's own charges, payouts, subscriptions, invoices, refunds and disputes; hover a tile for exactly what it counts.`,
                ...snap.notes,
              ].join(' ')}
              className="cursor-help"
            >
              <StatusPill tone="success">Stripe · {timeOf(snap.asOf)}</StatusPill>
            </span>
          )}
          <Select ariaLabel="Range" value={range} onChange={setRange} options={RANGE_OPTIONS} buttonClassName="min-w-[160px]" />
        </>
      }
    />
  )

  if (!snap) {
    // min-h-full: the panel's full height, so the ring centers in it under
    // the header rather than sitting just below it.
    return (
      <div className="flex min-h-full flex-col">
        {header}
        <PanelLoading label="Reading Stripe…" />
      </div>
    )
  }
  if (!snap.available) {
    return (
      <div>
        {header}
        <div className={BOX}>{snap.reason || 'Stripe could not be read.'}</div>
      </div>
    )
  }

  const s = snap.summary
  const inRange = SALES_RANGE_LABELS[range].toLowerCase()
  const priorNote = `vs the ${s.window.days} days before`
  const bucketWord = snap.series.bucket === 'week' ? 'week' : 'day'
  const bank = s.payouts.destinations.length === 1 ? s.payouts.destinations[0] : ''
  const inStripe = s.balance.inStripeCents
  const payoutNote = [
    inStripe ? `${money(inStripe)} still in Stripe` : plural(s.payouts.count, 'payout'),
    s.payouts.onTheWayCents ? `${money(s.payouts.onTheWayCents)} on the way` : '',
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div>
      {header}

      <section className={`mb-3 ${KPI_ROW}`}>
        <KpiShell
          label="Revenue"
          icon={ForecastIcon}
          labelTitle={`Money collected in the range — special deals and subscription invoices alike — less refunds issued in it${s.disputes ? ' and disputes opened in it' : ''}. Stripe's fees are not taken off; they came to ${money(s.feesCents)}. Delta is ${priorNote}.`}
          note={`${money(s.feesCents)} Stripe fees`}
          delta={deltaOf(s.netRevenueCents, s.priorNetRevenueCents, 'up')}
        >
          {money(s.netRevenueCents)}
        </KpiShell>
        <KpiShell
          label="Special deals"
          icon={TagIcon}
          labelTitle={`Payments in the range that are not subscription invoices: sales by payment link (each named after its link), one-off invoices and direct charges. ${plural(s.paymentLinks, 'payment')} came by payment link (${money(s.paymentLinksCents)}). Delta is ${priorNote}.`}
          note={`${plural(s.specialDeals, 'payment')} · ${s.paymentLinks} by link`}
          delta={deltaOf(s.specialDealsCents, s.priorSpecialDealsCents, 'up')}
        >
          {money(s.specialDealsCents)}
        </KpiShell>
        <KpiShell
          label="Subscriptions"
          icon={RevenueSignalsIcon}
          labelTitle={`Subscription invoices paid in the range, retries included. Delta is ${priorNote}.`}
          note={`${plural(s.subscriptionPayments, 'invoice')} paid`}
          delta={deltaOf(s.subscriptionRevenueCents, s.priorSubscriptionRevenueCents, 'up')}
        >
          {money(s.subscriptionRevenueCents)}
        </KpiShell>
        <KpiShell
          label="Payouts"
          icon={CheckSquareIcon}
          labelTitle={`Transfers that landed in the bank in the range${bank ? ` (${bank})` : ''}: ${plural(s.payouts.count, 'payout')}. Still in Stripe is the balance it holds now — ${money(s.balance.availableCents)} available, ${money(s.balance.pendingCents)} pending — which the next payouts will carry.${s.payouts.failed ? ` ${plural(s.payouts.failed, 'payout')} failed in the range.` : ''} Delta is ${priorNote}.`}
          note={payoutNote}
          delta={deltaOf(s.payouts.paidCents, s.payouts.priorPaidCents, 'up')}
        >
          {money(s.payouts.paidCents)}
        </KpiShell>
      </section>

      <section className={`mb-6 ${KPI_ROW}`}>
        <KpiShell
          label="MRR"
          icon={RevenueSignalsIcon}
          labelTitle={
            "Monthly recurring revenue, Stripe Billing's definition: every active or past-due subscription's price × seats, normalized to a month (a yearly plan counts a twelfth), less recurring discounts, before tax — counted from the subscription's first paid invoice. " +
            `Trials, paused, canceled and never-paid subscriptions do not count${s.awaitingFirstPayment.count ? ` (${plural(s.awaitingFirstPayment.count, 'past-due subscription')} that never paid would add ${money(s.awaitingFirstPayment.mrrCents)})` : ''}, and special deals are never MRR. ` +
            `${plural(s.activeSubscribers, 'subscriber')}, ${s.trialing} trialing. As of now; the range does not change it. Delta is vs the end of last month.`
          }
          note={`${money(s.arrCents)} ARR · ${plural(s.activeSubscribers, 'subscriber')}`}
          delta={s.mrrDeltaVsPriorMonth}
        >
          {money(s.mrrCents)}
        </KpiShell>
        <KpiShell
          label="Refunds"
          icon={RevenueSignalsIcon}
          labelTitle={`Refunds issued in the range, on the day Stripe issued them — not the day of the payment they returned. A rising figure paints red. Delta is ${priorNote}.`}
          note={`${plural(s.refunds, 'refund')} · ${inRange}`}
          delta={deltaOf(s.refundsCents, s.priorRefundsCents, 'down')}
        >
          {money(s.refundsCents)}
        </KpiShell>
        <KpiShell
          label="Failed payments"
          icon={MailIcon}
          labelTitle={`Payment attempts that were declined in the range. Some were retried and later paid; the ones still owed are the past-due invoices. Delta is ${priorNote}.`}
          note={`${money(s.paymentsFailedCents)} declined`}
          delta={deltaOf(s.paymentsFailed, s.priorPaymentsFailed, 'down')}
        >
          {s.paymentsFailed.toLocaleString()}
        </KpiShell>
        <KpiShell
          label="Past-due invoices"
          icon={ClockIcon}
          labelTitle={`Open invoices with a failed payment that Stripe is still collecting, as of now — the range does not change it.${s.abandonedInvoices ? ` Another ${plural(s.abandonedInvoices, 'invoice')} (${money(s.abandonedCents)}) are still open on subscriptions Stripe has since canceled, and are not counted.` : ''}`}
          note={`${money(s.pastDueCents)} outstanding`}
        >
          {s.pastDueInvoices.toLocaleString()}
        </KpiShell>
      </section>

      <section className={CARD}>
        <h3
          className="m-0 mb-3 text-[14px] font-semibold text-ink"
          title={`Each bar is the money collected that ${bucketWord}: ${money(s.collectedCents)} across ${inRange}, ${money(s.specialDealsCents)} of it special deals. Refunds are taken off on the Revenue tile, not here, so a bar is exactly what came in.`}
        >
          Revenue collected — per {bucketWord}
        </h3>
        {revenueBars.length ? (
          <BarChart data={revenueBars} keyOf={(d) => d.key} labelEvery="auto" height={180} formatValue={dollars} />
        ) : (
          <div className="py-6 text-center text-[13px] text-faint">No days in range.</div>
        )}
      </section>

      <h2 className={SECTION_H}>Payments</h2>
      <div className="mb-6">
        <DataTable
          tableId="sales-payments"
          columns={PAYMENT_COLUMNS}
          rows={payments}
          rowKey={(p) => p.id}
          searchable
          searchPlaceholder="Search payments…"
          searchValue={(p) => `${p.customer.name} ${p.customer.email} ${p.description} ${SOURCE_LABEL[p.source] || ''} ${p.method} ${p.id} ${PAYMENT_LABEL[p.status] || p.status}`}
          tabs={paymentTabs}
          activeTab={paymentTab}
          onTabChange={setPaymentTab}
          pageSize={20}
          emptyMessage={`No payments in ${inRange}.`}
        />
      </div>

      <h2 className={SECTION_H}>Payouts</h2>
      <div className="mb-6">
        <DataTable
          tableId="sales-payouts"
          columns={PAYOUT_COLUMNS}
          rows={payouts}
          rowKey={(p) => p.id}
          pageSize={10}
          emptyMessage={`No payouts landed in ${inRange}.`}
        />
      </div>

      <h2 className={SECTION_H}>Subscriptions</h2>
      <div className="mb-6">
        <DataTable
          tableId="sales-subscriptions"
          columns={SUBSCRIPTION_COLUMNS}
          rows={subscriptions}
          rowKey={(row) => row.id}
          searchable
          searchPlaceholder="Search subscriptions…"
          searchValue={(row) => `${row.customer.name} ${row.customer.email} ${row.planName} ${row.deal} ${row.id} ${SUB_LABEL[row.status] || row.status}`}
          tabs={subscriptionTabs}
          activeTab={subscriptionTab}
          onTabChange={setSubscriptionTab}
          pageSize={20}
          emptyMessage="No subscriptions."
        />
      </div>
    </div>
  )
}
